class ProductSelector extends HTMLElement {
  constructor() {
    super();
    this.inputWrapper = '[data-input-wrapper]';
    this.variants = JSON.parse(
      this.querySelector('[type="application/json"]').textContent
    );
    this.form = document.querySelector(
      `#ProductForm-${this.dataset.sectionId}`
    );
    this.purchaseOptions = document.querySelector(
      `#Product-Purchase-Options-${this.dataset.sectionId}`
    );
    this.productBar = document.querySelector(
      `#ProductBar-${this.dataset.sectionId}`
    );
    this.installmentsForm = document.querySelector(
      `#Product-Installments-${this.dataset.installmentsFormId}`
    );

    this.addEventListener('change', this.onVariantChange.bind(this));

    this.updateOptions();
    this.updateVariantStatuses();
    this.updateVariant();
    this.initUpdateMedia();

    if (this.needsSize()) {
      this.setChooseSize();
    } else if (this.form && this.currentVariant) {
      this.form.toggleAddButton(!this.currentVariant.available, '');
      this.updateAddToCartColor();
    }
    this.syncSecondColor();
    this.updateSecondColorAvailability();
  }

  updateVariantStatuses() {
    const checkedOptionOne = this.querySelector(this.inputWrapper)?.querySelector(':checked');
    const optionOneValue = checkedOptionOne?.value;
    const selectedOptionOneVariants = optionOneValue
      ? this.variants.filter(variant => optionOneValue === variant.option1)
      : this.variants;
    const inputWrappers = [
      ...this.querySelectorAll(this.inputWrapper)
    ];

    inputWrappers.forEach((option, index) => {
      if (index === 0) return;

      const previousChecked = inputWrappers[index - 1].querySelector(':checked');
      const isColorOption = Boolean(option.querySelector('.product__swatch--color'));
      if (!previousChecked && !isColorOption) return;

      const listOfAvailableOptions = previousChecked
        ? selectedOptionOneVariants
          .filter(
            variant =>
              variant.available &&
              variant[`option${index}`] === previousChecked.value
          )
          .map(variantOption => variantOption[`option${index + 1}`])
        : this.variants
          .filter(variant => variant.available)
          .map(variant => variant[`option${index + 1}`]);

      const optionInputs = [
        ...option.querySelectorAll('input[type="radio"], option')
      ];

      optionInputs.forEach(input => {
        const inputIsDisabled = !listOfAvailableOptions.includes(
          input.getAttribute('value')
        );

        if (input.getAttribute('data-stock')) {
          return
        } else {
          input.disabled = inputIsDisabled;
        }
        if (input.tagName.toLowerCase() === 'option') {
          const escapedValue = input.value.replace(/"/g, '\\"');
          const selector = `button[data-value="${escapedValue}"]`;
          const button = input
            .closest('dropdown-input')
            .querySelector(selector);
          if (button) {
            button.disabled = inputIsDisabled;
          }
        }
        if (!this.productBar) return;
        const inputId = input.dataset.id || input.id;
        const productBarInput = this.productBar.querySelector(
          `option[data-id="${inputId}"]`
        );
        if (!productBarInput) return;
        productBarInput.disabled = inputIsDisabled;
      });
    });
  }

  onVariantChange(event) {
    if (event.target.type === 'number') return;
    if (event.target.closest('.product-selector__second-color')) {
      event.target.closest('.product-selector__second-color').dataset.picked = 'true';
      this.markSecondColorActive();
      return;
    }
    this.updateOptions();

    if (this.needsSize()) {
      this.setChooseSize();
      this.updateVariantStatuses();
      this.syncSecondColor();
      this.updateSecondColorAvailability();
      return;
    }

    this.clearChooseSize();
    this.updateAddToCartColor();
    if (this.currentVariant === this.getVariantData()) {
      this.syncSecondColor();
      this.updateSecondColorAvailability();
      return;
    }

    this.updateVariant();
    this.updateVariantStatuses();
    this.productBarUpdateOptions();
    this.updatePickupAvailability();
    if (this.form) {
      // this.form.removeAttribute('data-has-selling-plan'); // removing this is causing issue for selling plans
      this.form.toggleAddButton(false, '');
      this.form.handleErrorMessage();
    }

    if (!this.currentVariant) {
      if (this.form) {
        this.form.toggleAddButton(true, '');
      }
      this.setUnavailable();
      this.syncSecondColor();
      this.updateSecondColorAvailability();
      return;
    }

    if (!this.currentVariant.available) {
      if (this.form) {
        this.form.toggleAddButton(true, window.variantStrings.soldOut);
      }
    }

    this.updateMedia();
    this.updateURL();
    this.updateVariantInput();
    this.renderProductInfo();
    this.syncSecondColor();
    this.updateSecondColorAvailability();
  }

  updateOptions() {
    this.querySelectorAll(`${this.inputWrapper} input[type="radio"]`).forEach(input => {
      input.nextElementSibling.classList.toggle(
        'is-active',
        input.checked
      );
    });
    this.options = Array.from(
      this.querySelectorAll(`${this.inputWrapper} input[type="radio"]:checked, ${this.inputWrapper} select`),
      el => ({ name: el.dataset.name || el.name, value: el.value })
    );
  }

  productBarUpdateOptions() {
    if (this.productBar) {
      Array.from(this.productBar.querySelectorAll('select')).map(
        selector => {
          const matchedOption = this.options.find(
            option => option.name === selector.name
          );
          if (!matchedOption) return;
          selector.value = matchedOption.value;
        }
      );
      this.productBar.querySelectorAll('dropdown-input').forEach(
        dropdown => {
          dropdown.update();
        }
      );
    }
  }

  updateInventoryNotice(html) {
    const destination = document.querySelector('[data-inventory-notice]');
    const source = html.querySelector('[data-inventory-notice]');

    if (source && destination) destination.innerHTML = source.innerHTML;

    // Pass the inventory quantity from source to the quantity input
    const inventoryQuantityMatch = source?.textContent.match(/\d+/);
    const inventoryQuantity = inventoryQuantityMatch ? parseInt(inventoryQuantityMatch[0]) : 0;
    this.updateQuantityInput(inventoryQuantity);
  }

  updateQuantityInput(inventoryQuantity) {
    const selectedVariantData = this.getVariantData();
    const quantityInput = document.querySelector('.product-selector__quantity quantity-input input');

    let productUsesInventory = true;

    // check inventory is active
    if (selectedVariantData && selectedVariantData.inventory_management === null) {
      productUsesInventory = false;
    }

    // check sell on sold out is active
    if (selectedVariantData && selectedVariantData.inventory_quantity !== null && inventoryQuantity === 0 && selectedVariantData.available === true) {
      productUsesInventory = false;
    }

    if (quantityInput) {
      if (productUsesInventory) {
        quantityInput.setAttribute('max', inventoryQuantity);
        // set value accordingly to inventory
        if (quantityInput.value > inventoryQuantity) {
          quantityInput.value = inventoryQuantity;
        }
      } else {
        quantityInput.setAttribute('max', '');
      }
    }
  }

  updatePickupAvailability() {
    const pickUpAvailabilityNode = document.querySelector(
      'pickup-availability'
    );

    if (!pickUpAvailabilityNode || !this.currentVariant) return;

    pickUpAvailabilityNode.dataset.variantId = this.currentVariant.id;

    if (this.currentVariant && this.currentVariant.available) {
      pickUpAvailabilityNode.setAttribute('available', '');
      pickUpAvailabilityNode.fetchAvailability(
        pickUpAvailabilityNode.dataset.variantId
      );

      return;
    }

    pickUpAvailabilityNode.removeAttribute('available');
    pickUpAvailabilityNode.innerHTML = '';
  }

  updateVariant() {
    this.currentVariant = this.getVariantData();
  }

  getSecondVariant() {
    const fieldset = this.querySelector('.product-selector__second-color');
    const current = this.getVariantData();
    if (!fieldset) return current;

    const color = fieldset.querySelector('input[type="radio"]:checked')?.value;
    if (!color) return null;

    const wrappers = [...this.querySelectorAll(this.inputWrapper)];
    const colorIndex = wrappers.findIndex(wrapper => wrapper.querySelector('.product__swatch--color'));
    if (colorIndex < 0) return current;

    const selections = wrappers.map((wrapper, index) => {
      if (index === colorIndex) return { index: index + 1, value: color };
      return { index: index + 1, value: wrapper.querySelector(':checked')?.value };
    });
    if (selections.some(selection => !selection.value)) return null;

    return this.variants.find(variant =>
      selections.every(selection => variant[`option${selection.index}`] === selection.value)
    ) || null;
  }

  syncSecondColor() {
    const fieldset = this.querySelector('.product-selector__second-color');
    if (!fieldset || fieldset.dataset.picked === 'true') {
      this.markSecondColorActive();
      return;
    }

    const checked = [...this.querySelectorAll(`${this.inputWrapper} input[type="radio"]`)]
      .find(input => input.checked && input.nextElementSibling?.classList.contains('product__swatch--color'));
    if (!checked) return;

    const match = [...fieldset.querySelectorAll('input[type="radio"]')]
      .find(input => input.value === checked.value && !input.disabled);
    if (match) match.checked = true;
    this.markSecondColorActive();
  }

  markSecondColorActive() {
    this.querySelectorAll('.product-selector__second-color input[type="radio"]').forEach(input => {
      input.nextElementSibling?.classList.toggle('is-active', input.checked);
    });
  }

  updateSecondColorAvailability() {
    const fieldset = this.querySelector('.product-selector__second-color');
    if (!fieldset) return;

    const wrappers = [...this.querySelectorAll(this.inputWrapper)];
    const colorIndex = wrappers.findIndex(wrapper => wrapper.querySelector('.product__swatch--color'));
    if (colorIndex < 0) return;

    const otherSelections = wrappers.flatMap((wrapper, index) => {
      if (index === colorIndex) return [];
      return [{ index: index + 1, value: wrapper.querySelector(':checked')?.value }];
    });

    fieldset.querySelectorAll('input[type="radio"]').forEach(input => {
      input.disabled = !this.variants.some(variant =>
        variant.available &&
        variant[`option${colorIndex + 1}`] === input.value &&
        otherSelections.every(selection => !selection.value || variant[`option${selection.index}`] === selection.value)
      );
    });

    const checked = fieldset.querySelector('input[type="radio"]:checked');
    if (checked?.disabled) {
      const fallback = [...fieldset.querySelectorAll('input[type="radio"]')].find(input => !input.disabled);
      if (fallback) fallback.checked = true;
    }
    this.markSecondColorActive();
  }

  getVariantData() {
    const optionGroups = this.querySelectorAll(this.inputWrapper);
    if (optionGroups.length > 0 && this.options.length < optionGroups.length) {
      return null;
    }

    if (this.options.length === 0) {
      return this.variants[0]; // Return the first variant if no options are selected
    }

    return this.variants.find(variant => {
      // Check if the variant's options match the selected options
      return this.options.every(option => {
        const optionIndex = this.options.findIndex(opt => opt.name === option.name) + 1;
        return variant[`option${optionIndex}`] === option.value;
      });
    });
  }

  initUpdateMedia() {

    const urlParams = new URLSearchParams(window.location.search);
    const variantIdFromUrl = parseInt(urlParams.get('variant'));

    if (variantIdFromUrl) {
      this.currentVariant = this.variants.find(variant => variant.id === variantIdFromUrl);

      if (this.currentVariant) {
        this.updateMedia();
      }
    }
  }

  updateMedia() {
    if (!this.currentVariant || !this.currentVariant.featured_media)
      return;

    const productMedia = document.querySelector('product-media');
    productMedia.setActiveMedia(
      this.currentVariant.featured_media.id
    );
  }

  updateURL(sellingPlanId) {
    if (!this.currentVariant) return;
    const params = new URLSearchParams(window.location.search);
    params[params.has('variant') ? 'set' : 'append'](
      'variant',
      this.currentVariant.id
    );
    if (sellingPlanId) {
      params[params.has('selling_plan') ? 'set' : 'append'](
        'selling_plan',
        sellingPlanId
      );
    } else {
      params.delete('selling_plan');
    }
    window.history.replaceState(
      {},
      '',
      `${this.dataset.url}?${params.toString()}`
    );
  }

  updateVariantInput() {
    const inputs = [
      this.querySelector('[name="id"]'),
      this.installmentsForm?.querySelector('[name="id"]')
    ];
    inputs.forEach(input => {
      if (!input) return;
      input.value = this.currentVariant.id;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });
  }

  setUnavailable() {
    const submitButton = this.form.submitButton;
    const price = this.querySelector(
      `#Product-Price-${this.dataset.sectionId}`
    );

    if (!submitButton) return;
    submitButton.textContent = window.variantStrings.unavailable;
    if (price) price.classList.add('visibility-hidden');
  }

  needsSize() {
    const optionGroups = this.querySelectorAll(this.inputWrapper);
    return optionGroups.length > 0 && this.options.length < optionGroups.length;
  }

  chooseSizeButtons() {
    return [
      this.form?.submitButton,
      this.productBar?.querySelector('[name="add"]')
    ];
  }

  setChooseSize() {
    this.currentVariant = null;
    this.chooseSizeButtons().forEach(button => {
      if (!button) return;
      button.textContent = window.variantStrings.chooseSize;
      button.classList.add('product-selector__submit--choose-size');
      button.removeAttribute('disabled');
      button.style.setProperty('--color-button-text', '#000000');
      button.style.setProperty('--color-button-background', '#F5EBDF');
      button.style.setProperty('--color-button-outline', '#F5EBDF');
      button.style.setProperty('--color-button-hover-text', '#000000');
      button.style.setProperty('--color-button-hover-background', '#F5EBDF');
      button.style.setProperty('--color-button-hover-outline', '#F5EBDF');
    });

    const idInput = this.querySelector('[name="id"]');
    if (idInput) idInput.value = '';
  }

  clearChooseSize() {
    this.chooseSizeButtons().forEach(button => {
      button?.classList.remove('product-selector__submit--choose-size');
    });
  }

  updateAddToCartColor() {
    const colorSwatch = this.querySelector(
      'input:checked + .product__swatch--color'
    );
    if (!colorSwatch) return;

    const background = getComputedStyle(colorSwatch).backgroundColor;
    const match = background.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*([\d.]+))?\)/);
    if (!match || match[4] === '0') return;

    const red = Number(match[1]);
    const green = Number(match[2]);
    const blue = Number(match[3]);
    const text = this.contrastText(red, green, blue);
    const color = `rgb(${red}, ${green}, ${blue})`;
    const buttons = [
      this.form?.querySelector('.product-selector__submit'),
      this.productBar?.querySelector('[name="add"]')
    ];

    buttons.forEach(button => {
      if (!button) return;
      button.style.setProperty('--color-button-text', text);
      button.style.setProperty('--color-button-background', color);
      button.style.setProperty('--color-button-outline', color);
      button.style.setProperty('--color-button-hover-text', color);
      button.style.setProperty('--color-button-hover-background', text);
      button.style.setProperty('--color-button-hover-outline', color);
    });
  }

  contrastText(red, green, blue) {
    const channel = value => {
      const scaled = value / 255;
      return scaled <= 0.03928
        ? scaled / 12.92
        : Math.pow((scaled + 0.055) / 1.055, 2.4);
    };
    const luminance =
      0.2126 * channel(red) +
      0.7152 * channel(green) +
      0.0722 * channel(blue);

    return luminance > 0.45 ? '#111111' : '#ffffff';
  }

  renderProductInfo() {
    const params = new URLSearchParams(window.location.search);
    params[params.has('variant') ? 'set' : 'append'](
      'variant',
      this.currentVariant.id
    );
    params.append('section_id', this.dataset.sectionId);

    fetch(`${this.dataset.url}?${params.toString()}`)
      .then(response => response.text())
      .then(responseText => {
        const html = new DOMParser().parseFromString(
          responseText,
          'text/html'
        );
        [
          `#Product-Price-${this.dataset.sectionId}`,
          `#Product-Purchase-Options-${this.dataset.sectionId}`
        ].map(liveRegionSelector => {
          const destination = document.querySelector(
            liveRegionSelector
          );
          const source = html.querySelector(liveRegionSelector);

          this.updateInventoryNotice(html);

          if (destination && source) {
            destination.classList.remove('visibility-hidden');
            destination.innerHTML = source.innerHTML;
          }
        });
      });
  }
}

customElements.define('product-selector', ProductSelector);
